import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  uploadEmployeeExcel,
  listEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  markEmployeeLeft,
  rejoinEmployee,
} from '../actions/employeeMasterActions';
import { listActiveCompanies } from '../actions/companyActions';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Chip,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  ExitToApp as LeftIcon,
  PersonAdd as RejoinIcon,
} from '@mui/icons-material';
import {
  EMPLOYEE_UPLOAD_RESET,
  EMPLOYEE_CREATE_RESET,
  EMPLOYEE_UPDATE_RESET,
  EMPLOYEE_DELETE_RESET,
  EMPLOYEE_MARK_LEFT_RESET,
  EMPLOYEE_REJOIN_RESET,
} from '../constants/employeeMasterConstants';

const emptyEmployee = {
  employeeCode: '',
  firstName: '',
  middleName: '',
  lastName: '',
  jobTitle: '',
  mobile: '',
  alternateNumber: '',
  nameAsPerBank: '',
  bankName: '',
  accountNumber: '',
  ifscCode: '',
  accountType: 'SB',
  bankType: '',
  panCard: '',
  salary: '',
  tds: '',
  supervisorName: '',
  process: '',
  type: '',
  category: '',
  company: '',
};

const months = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const EmployeeMaster = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userLogin = useSelector((s) => s.userLogin);
  const { userInfo } = userLogin;
  const getRoleInfo = useSelector((s) => s.getRoleInfo);
  const role = getRoleInfo?.getRoleData || {};

  useEffect(() => {
    if (getRoleInfo?.getRoleData) {
      const r = getRoleInfo.getRoleData;
      if (r.employeeMasterView === false && r.employeeLeftView === false) {
        toast('Not Authorized', { type: 'error', hideProgressBar: true, autoClose: 2000 });
        navigate('/');
      }
    }
  }, [getRoleInfo?.getRoleData]);

  const [tabValue, setTabValue] = useState(0); // 0=Active, 1=Left
  const [filterCompany, setFilterCompany] = useState('');
  const [filterType, setFilterType] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCompany, setUploadCompany] = useState('');
  const [uploadType, setUploadType] = useState('');
  const [uploadDialog, setUploadDialog] = useState(false);

  // Add/Edit dialog
  const [formDialog, setFormDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState(emptyEmployee);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Mark Left dialog
  const [leftDialog, setLeftDialog] = useState(false);
  const [leftTarget, setLeftTarget] = useState(null);
  const [leftDate, setLeftDate] = useState(new Date().toISOString().split('T')[0]);

  // Redux state
  const employeeUpload = useSelector((s) => s.employeeUpload);
  const employeeList = useSelector((s) => s.employeeList);
  const { employees = [], total = 0 } = employeeList;
  const employeeCreate = useSelector((s) => s.employeeCreate);
  const employeeUpdate = useSelector((s) => s.employeeUpdate);
  const employeeDelete = useSelector((s) => s.employeeDelete);
  const employeeMarkLeft = useSelector((s) => s.employeeMarkLeft);
  const employeeRejoin = useSelector((s) => s.employeeRejoin);
  const companyList = useSelector((s) => s.companyList);
  const { companies = [] } = companyList;

  const isLeft = tabValue === 1;

  const fetchEmployees = () => {
    dispatch(listEmployees({
      company: filterCompany,
      type: filterType,
      isLeft,
      search,
      page: page + 1,
      limit: rowsPerPage,
    }));
  };

  useEffect(() => {
    dispatch(listActiveCompanies());
  }, [dispatch]);

  useEffect(() => {
    fetchEmployees();
  }, [dispatch, filterCompany, filterType, tabValue, search, page, rowsPerPage]);

  // Upload success/fail
  useEffect(() => {
    if (employeeUpload.success) {
      const r = employeeUpload.uploadResult;
      toast.success(`Upload complete! Created: ${r.success}, Updated: ${r.updated}, Failed: ${r.failed}`);
      setUploadDialog(false);
      setUploadFile(null);
      fetchEmployees();
      dispatch({ type: EMPLOYEE_UPLOAD_RESET });
    }
    if (employeeUpload.error) {
      toast.error(employeeUpload.error);
      dispatch({ type: EMPLOYEE_UPLOAD_RESET });
    }
  }, [employeeUpload.success, employeeUpload.error]);

  // Create success/fail
  useEffect(() => {
    if (employeeCreate.success) {
      toast.success('Employee created successfully!');
      setFormDialog(false);
      setFormData(emptyEmployee);
      fetchEmployees();
      dispatch({ type: EMPLOYEE_CREATE_RESET });
    }
    if (employeeCreate.error) {
      toast.error(employeeCreate.error);
      dispatch({ type: EMPLOYEE_CREATE_RESET });
    }
  }, [employeeCreate.success, employeeCreate.error]);

  // Update success/fail
  useEffect(() => {
    if (employeeUpdate.success) {
      toast.success('Employee updated successfully!');
      setFormDialog(false);
      fetchEmployees();
      dispatch({ type: EMPLOYEE_UPDATE_RESET });
    }
    if (employeeUpdate.error) {
      toast.error(employeeUpdate.error);
      dispatch({ type: EMPLOYEE_UPDATE_RESET });
    }
  }, [employeeUpdate.success, employeeUpdate.error]);

  // Delete success/fail
  useEffect(() => {
    if (employeeDelete.success) {
      toast.success('Employee deleted!');
      setDeleteDialog(false);
      fetchEmployees();
      dispatch({ type: EMPLOYEE_DELETE_RESET });
    }
    if (employeeDelete.error) {
      toast.error(employeeDelete.error);
      dispatch({ type: EMPLOYEE_DELETE_RESET });
    }
  }, [employeeDelete.success, employeeDelete.error]);

  // Mark Left success/fail
  useEffect(() => {
    if (employeeMarkLeft.success) {
      toast.success('Employee marked as left!');
      setLeftDialog(false);
      fetchEmployees();
      dispatch({ type: EMPLOYEE_MARK_LEFT_RESET });
    }
    if (employeeMarkLeft.error) {
      toast.error(employeeMarkLeft.error);
      dispatch({ type: EMPLOYEE_MARK_LEFT_RESET });
    }
  }, [employeeMarkLeft.success, employeeMarkLeft.error]);

  // Rejoin success/fail
  useEffect(() => {
    if (employeeRejoin.success) {
      toast.success('Employee rejoined!');
      fetchEmployees();
      dispatch({ type: EMPLOYEE_REJOIN_RESET });
    }
    if (employeeRejoin.error) {
      toast.error(employeeRejoin.error);
      dispatch({ type: EMPLOYEE_REJOIN_RESET });
    }
  }, [employeeRejoin.success, employeeRejoin.error]);

  const handleUpload = () => {
    if (!uploadFile) return toast.error('Please select a file');
    if (!uploadCompany) return toast.error('Please select a company');
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('company', uploadCompany);
    if (uploadType) fd.append('type', uploadType);
    dispatch(uploadEmployeeExcel(fd));
  };

  const handleOpenAdd = () => {
    setEditMode(false);
    setFormData(emptyEmployee);
    setFormDialog(true);
  };

  const handleOpenEdit = (emp) => {
    setEditMode(true);
    setSelectedEmployee(emp);
    setFormData({
      employeeCode: emp.employeeCode || '',
      firstName: emp.firstName || '',
      middleName: emp.middleName || '',
      lastName: emp.lastName || '',
      jobTitle: emp.jobTitle || '',
      mobile: emp.mobile || '',
      alternateNumber: emp.alternateNumber || '',
      nameAsPerBank: emp.nameAsPerBank || '',
      bankName: emp.bankName || '',
      accountNumber: emp.accountNumber || '',
      ifscCode: emp.ifscCode || '',
      accountType: emp.accountType || 'SB',
      bankType: emp.bankType || '',
      panCard: emp.panCard || '',
      salary: emp.salary || '',
      tds: emp.tds || '',
      supervisorName: emp.supervisorName || '',
      process: emp.process || '',
      type: emp.type || '',
      category: emp.category || '',
      company: emp.company?._id || emp.company || '',
    });
    setFormDialog(true);
  };

  const handleFormSubmit = () => {
    if (!formData.employeeCode) return toast.error('Employee code is required');
    if (!formData.company) return toast.error('Company is required');
    if (editMode) {
      dispatch(updateEmployee(selectedEmployee._id, formData));
    } else {
      dispatch(createEmployee(formData));
    }
  };

  const handleMarkLeft = () => {
    dispatch(markEmployeeLeft(leftTarget._id, leftDate));
  };

  const handleRejoin = (emp) => {
    dispatch(rejoinEmployee(emp._id));
  };

  const handleDelete = () => {
    dispatch(deleteEmployee(deleteTarget._id));
  };

  const salaryTypes = ['Collection', 'Verification', 'Standard'];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" mb={2}>Employee Master</Typography>

      {/* Top Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Company</InputLabel>
              <Select value={filterCompany} onChange={(e) => { setFilterCompany(e.target.value); setPage(0); }} label="Company">
                <MenuItem value="">All Companies</MenuItem>
                {companies.map((c) => <MenuItem key={c._id} value={c._id}>{c.companyName}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select value={filterType} onChange={(e) => { setFilterType(e.target.value); setPage(0); }} label="Type">
                <MenuItem value="">All Types</MenuItem>
                {salaryTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth size="small" label="Search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs="auto">
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchEmployees}>Refresh</Button>
          </Grid>
          {(isLeft ? role.employeeLeftUpload !== false : role.employeeMasterUpload !== false) && (
            <Grid item xs="auto">
              <Button variant="contained" color="primary" startIcon={<UploadIcon />} onClick={() => setUploadDialog(true)}>
                Upload Excel
              </Button>
            </Grid>
          )}
          {(isLeft ? role.employeeLeftAdd !== false : role.employeeMasterAdd !== false) && (
            <Grid item xs="auto">
              <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={handleOpenAdd}>
                Add Employee
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Tabs: Active / Left */}
      <Tabs value={tabValue} onChange={(e, v) => { setTabValue(v); setPage(0); }} sx={{ mb: 2 }}>
        <Tab label="Active Employees" />
        <Tab label="Left Employees" />
      </Tabs>

      {/* Table */}
      <Paper>
        {employeeList.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: '#f5f5f5', whiteSpace: 'nowrap' } }}>
                    <TableCell>Emp Code</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>Middle Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Job Title</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell>Alt. Number</TableCell>
                    <TableCell>Name (Bank)</TableCell>
                    <TableCell>Bank Name</TableCell>
                    <TableCell>Account No.</TableCell>
                    <TableCell>IFSC</TableCell>
                    <TableCell>Acc. Type</TableCell>
                    <TableCell>Bank Type</TableCell>
                    <TableCell>PAN</TableCell>
                    <TableCell>Salary</TableCell>
                    <TableCell>TDS %</TableCell>
                    <TableCell>Supervisor</TableCell>
                    <TableCell>Process</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Company</TableCell>
                    {isLeft && <TableCell>Left Date</TableCell>}
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isLeft ? 23 : 22} align="center">No employees found</TableCell>
                    </TableRow>
                  ) : employees.map((emp) => (
                    <TableRow key={emp._id} hover>
                      <TableCell>{emp.employeeCode}</TableCell>
                      <TableCell>{emp.firstName}</TableCell>
                      <TableCell>{emp.middleName}</TableCell>
                      <TableCell>{emp.lastName}</TableCell>
                      <TableCell>{emp.jobTitle}</TableCell>
                      <TableCell>{emp.mobile}</TableCell>
                      <TableCell>{emp.alternateNumber}</TableCell>
                      <TableCell>{emp.nameAsPerBank}</TableCell>
                      <TableCell>{emp.bankName}</TableCell>
                      <TableCell>{emp.accountNumber}</TableCell>
                      <TableCell>{emp.ifscCode}</TableCell>
                      <TableCell>{emp.accountType}</TableCell>
                      <TableCell>{emp.bankType}</TableCell>
                      <TableCell>{emp.panCard}</TableCell>
                      <TableCell>₹{Number(emp.salary || 0).toLocaleString()}</TableCell>
                      <TableCell>{emp.tds || '-'}</TableCell>
                      <TableCell>{emp.supervisorName}</TableCell>
                      <TableCell>{emp.process}</TableCell>
                      <TableCell>{emp.category || '-'}</TableCell>
                      <TableCell>
                        {emp.type && <Chip label={emp.type} size="small" color={emp.type === 'Collection' ? 'primary' : emp.type === 'Verification' ? 'secondary' : 'default'} />}
                      </TableCell>
                      <TableCell>{emp.company?.companyName || '-'}</TableCell>
                      {isLeft && <TableCell>{emp.leftDate ? new Date(emp.leftDate).toLocaleDateString('en-IN') : '-'}</TableCell>}
                      <TableCell align="center">
                        {(isLeft ? role.employeeLeftUpdate !== false : role.employeeMasterUpdate !== false) && (
                          <Tooltip title="Edit">
                            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(emp)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!isLeft && role.employeeMasterMarkLeft !== false && (
                          <Tooltip title="Mark as Left">
                            <IconButton size="small" color="warning" onClick={() => { setLeftTarget(emp); setLeftDialog(true); }}>
                              <LeftIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isLeft && role.employeeLeftRejoin !== false && (
                          <Tooltip title="Rejoin">
                            <IconButton size="small" color="success" onClick={() => handleRejoin(emp)}>
                              <RejoinIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(isLeft ? role.employeeLeftDelete !== false : role.employeeMasterDelete !== false) && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => { setDeleteTarget(emp); setDeleteDialog(true); }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onClose={() => { setUploadDialog(false); setUploadFile(null); setUploadType(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Employee Excel</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Company *</InputLabel>
                <Select value={uploadCompany} onChange={(e) => setUploadCompany(e.target.value)} label="Company *">
                  {companies.map((c) => <MenuItem key={c._id} value={c._id}>{c.companyName}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select value={uploadType} onChange={(e) => setUploadType(e.target.value)} label="Type">
                  <MenuItem value="">None</MenuItem>
                  {salaryTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" startIcon={<UploadIcon />} fullWidth>
                {uploadFile ? uploadFile.name : 'Choose Excel File'}
                <input type="file" hidden accept=".xlsx,.xls,.csv" onChange={(e) => setUploadFile(e.target.files[0])} />
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="text"
                size="small"
                href="/employee_master_template.xlsx"
                download="employee_master_template.xlsx"
              >
                Download Template
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialog(false); setUploadFile(null); setUploadType(''); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={employeeUpload.loading}
            startIcon={employeeUpload.loading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {employeeUpload.loading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={formDialog} onClose={() => setFormDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {[
              { label: 'Employee Code *', key: 'employeeCode' },
              { label: 'First Name', key: 'firstName' },
              { label: 'Middle Name', key: 'middleName' },
              { label: 'Last Name', key: 'lastName' },
              { label: 'Job Title', key: 'jobTitle' },
              { label: 'Mobile', key: 'mobile' },
              { label: 'Alternate Number', key: 'alternateNumber' },
              { label: 'Name As Per Bank', key: 'nameAsPerBank' },
              { label: 'Bank Name', key: 'bankName' },
              { label: 'Account Number', key: 'accountNumber' },
              { label: 'IFSC Code', key: 'ifscCode' },
              { label: 'PAN Card', key: 'panCard' },
              { label: 'Salary', key: 'salary', type: 'number' },
              { label: 'TDS %', key: 'tds', type: 'number' },
              { label: 'Supervisor Name', key: 'supervisorName' },
              { label: 'Process', key: 'process' },
              { label: 'Category', key: 'category', type: 'number' },
            ].map(({ label, key, type }) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  fullWidth size="small" label={label} type={type || 'text'}
                  value={formData[key]}
                  onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                />
              </Grid>
            ))}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Account Type</InputLabel>
                <Select value={formData.accountType} onChange={(e) => setFormData({ ...formData, accountType: e.target.value })} label="Account Type">
                  <MenuItem value="SB">SB</MenuItem>
                  <MenuItem value="CB">CB</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Bank Type</InputLabel>
                <Select value={formData.bankType} onChange={(e) => setFormData({ ...formData, bankType: e.target.value })} label="Bank Type">
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Bob">Bob</MenuItem>
                  <MenuItem value="Non Bob">Non Bob</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} label="Type">
                  <MenuItem value="">None</MenuItem>
                  {salaryTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Company *</InputLabel>
                <Select value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} label="Company *">
                  {companies.map((c) => <MenuItem key={c._id} value={c._id}>{c.companyName}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleFormSubmit}
            disabled={employeeCreate.loading || employeeUpdate.loading}
          >
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark Left Dialog */}
      <Dialog open={leftDialog} onClose={() => setLeftDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark Employee as Left</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={2}>
            Employee: <strong>{leftTarget?.employeeCode} - {leftTarget?.firstName} {leftTarget?.lastName}</strong>
          </Typography>
          <TextField
            fullWidth size="small" label="Left Date" type="date"
            value={leftDate}
            onChange={(e) => setLeftDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeftDialog(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleMarkLeft} disabled={employeeMarkLeft.loading}>
            {employeeMarkLeft.loading ? 'Marking...' : 'Mark as Left'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Employee</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteTarget?.employeeCode} - {deleteTarget?.firstName} {deleteTarget?.lastName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={employeeDelete.loading}>
            {employeeDelete.loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeMaster;
